import React, { useState, useEffect, useRef } from 'react';
import { Link, useParams, useLocation, useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { cloneDeep, isEmpty } from 'lodash';
import { useTranslation } from 'react-i18next';
import { Col, Container, Row } from 'react-bootstrap';
import { Typeahead } from 'react-bootstrap-typeahead';
import '../CareLabel.css';
import Loader from '../../../../Components/Utility/Loader/Loader';
import noImage from '../../../../assets/images/no-image.png';
import * as action from '../store/CareLabel.action';
import 'react-bootstrap-typeahead/css/Typeahead.css';
import UploadImageModal from './UploadImageModal';
import { checkArray, imageUrl } from '../../../../Components/helper';
import { toasterMsg } from '../../../../Components/Toaster/Toaster';
import SymbolTableGroup from './SymbolTableGroup';
import InstructionTableGroup from './InstructionTableGroup';
import DisclaimerTableGroup from './DisclaimerTableGroup';
import { useComponent } from '../../../UserSetting/UsersLibrary/UserRights';
import ConfirmationPopup from '../../../../Components/ModalPopup/ConfirmationPopup';
import * as systemMasterAction from '../../../SystemMasters/Store/SystemMasters.action';

function AddEdit() {
  const {
    CareLabelReducer: { CareGropSingleData, AllCareLibraryList },
    UserDetails: { userInfo },
    Users: { logInRights },
  } = useSelector((reduxState) => reduxState);
  const history = useHistory();
  const dispatch = useDispatch();
  const childRef = useRef();
  const typeaheadRef = useRef(null);
  const { CheckActionPermission } = useComponent();
  const CareAddEditAccess = CheckActionPermission(logInRights, 'libraries', 'care', 'addEdit');
  const CareDeleteAccess = CheckActionPermission(logInRights, 'libraries', 'care', 'delete');

  const [state, setState] = useState({
    productTypeId: null,
    pageLoader: false,
    careGroupDetails: {},
    InstructionsData: [],
    SymbolData: [],
    DisclaimerData: [],
    uploadImageModal: false,
    isEditMode: false,
    checkedIds: [],
    isPageDisable: false,
    deleteConfirmModal: false,
    addToLibState: [],
    errorFields: [],
  });
  const {
    pageLoader,
    careGroupDetails,
    InstructionsData,
    SymbolData,
    DisclaimerData,
    uploadImageModal,
    isEditMode,
    checkedIds,
    isPageDisable,
    deleteConfirmModal,
    addToLibState,
  } = state;
  const { t } = useTranslation('common');

  const { groupId } = useParams();
  const location = useLocation();
  const addToLibraryData = location.state;
  const symbolsId = SymbolData.map((obj) => obj._id);
  const instrucionId = InstructionsData.map((obj) => obj._id);
  const disclaimerId = DisclaimerData.map((obj) => obj._id);

  const isAllSymbolChecked = symbolsId.length > 0 ? symbolsId.every((item) => checkedIds.includes(item)) : false;
  const isAllInstructionChecked = instrucionId.length > 0 ? instrucionId.every((item) => checkedIds.includes(item)) : false;
  const isAllDisclaimerChecked = disclaimerId.length > 0 ? disclaimerId.every((item) => checkedIds.includes(item)) : false;

  const setImage = (item) => {
    let imagePath = '';
    if (item?.image && item.image.includes('data:image/png;base64')) {
      imagePath = item.image;
    } else if (item?.image) {
      imagePath = `${imageUrl}/${item.image}`;
    } else {
      imagePath = noImage;
    }
    return imagePath;
  };

  useEffect(() => {
    const Init = async () => {
      await dispatch(action.fetchAllCareLabelLibrary());
    };
    Init();
    return () => {
      dispatch(action.unmountCareGroup());
      setState((prevState) => ({
        ...prevState,
        careGroupDetails: {},
      }));
    };
  }, []);

  useEffect(() => {
    if (groupId) {
      const callFn = async () => {
        setState((prevState) => ({
          ...prevState,
          pageLoader: true,
        }));
        const grpObj = {
          groupId,
          loggedInUserId: userInfo?.userId,
        };
        const response = await dispatch(action.fetchCareLabelGroupByID(groupId, grpObj));
        if (response?.code === 200) {
          setState((prevState) => ({
            ...prevState,
            pageLoader: false,
            isEditMode: true,
            isPageDisable: true,
          }));
        }
      };
      callFn();
    }
  }, [dispatch, groupId]);

  // useEffect call when Edit page open and get data based on Group Id
  useEffect(() => {
    if (!isEmpty(CareGropSingleData)) {
      setState((prevState) => ({
        ...prevState,
        careGroupDetails: CareGropSingleData,
      }));
    }
  }, [CareGropSingleData]);

  // useEffect to add library added in List Page Add to Library in Group Feature
  useEffect(() => {
    if (!isEmpty(addToLibraryData)) {
      setState((prevState) => ({
        ...prevState,
        addToLibState: addToLibraryData,
      }));
    }
  }, [addToLibraryData]);

  // useEffect to add library added in List Page Add to Library in Group Feature
  useEffect(() => {
    if (!isEmpty(addToLibState) && !isEmpty(careGroupDetails?.careLibIds)) {
      const newData = addToLibState.filter((eleData) => !careGroupDetails?.careLibIds.includes(eleData));

      if (!isEmpty(newData)) {
        const data = cloneDeep(careGroupDetails);
        data.careLibIds = [...data.careLibIds, ...newData];
        setState((prevState) => ({
          ...prevState,
          careGroupDetails: data,
        }));
      }
    } else if (!isEmpty(addToLibState)) {
      const data = cloneDeep(careGroupDetails);
      data.careLibIds = addToLibState;
      setState((prevState) => ({
        ...prevState,
        careGroupDetails: data,
      }));
    }
  }, [addToLibState, careGroupDetails]);

  // useEffect call when Care Lib Id's add or update
  useEffect(() => {
    if (checkArray(careGroupDetails?.careLibIds) && checkArray(AllCareLibraryList?.list)) {
      setState((prevState) => ({
        ...prevState,
        SymbolData: AllCareLibraryList?.list?.filter((ele) => careGroupDetails?.careLibIds.includes(ele?._id) && ele.type === 'symbol'),
        InstructionsData: AllCareLibraryList?.list?.filter((ele) => careGroupDetails?.careLibIds.includes(ele?._id) && ele.type === 'instruction'),
        DisclaimerData: AllCareLibraryList?.list?.filter((ele) => careGroupDetails?.careLibIds.includes(ele?._id) && ele.type === 'disclaimer'),
      }));
    } else {
      setState((prevState) => ({
        ...prevState,
        SymbolData: [],
        InstructionsData: [],
        DisclaimerData: [],
      }));
    }
  }, [careGroupDetails, AllCareLibraryList]);

  const handleDelete = (delId) => {
    const data = cloneDeep(careGroupDetails);
    const newIdArray = data?.careLibIds.filter((eleId) => eleId !== delId);
    const updatedAddedLib = addToLibState?.filter((eleIdUpdate) => eleIdUpdate !== delId);

    data.careLibIds = newIdArray;
    setState((prevState) => ({
      ...prevState,
      careGroupDetails: data,
      addToLibState: updatedAddedLib,
    }));
  };

  const handleInput = (e, key) => {
    const newData = cloneDeep(careGroupDetails);
    newData[`${e?.target?.name}`] = e?.target?.value;
    if (key === 'productTypeId') {
      setState((prevState) => ({
        ...prevState,
        [key]: e,
      }));
    }
    setState((prevState) => ({
      ...prevState,
      careGroupDetails: newData,
    }));
  };

  const handleImageUpload = () => {
    setState((prevState) => ({
      ...prevState,
      uploadImageModal: true,
    }));
  };

  const handleDeleteImage = () => {
    setState((prevState) => ({
      ...prevState,
      deleteConfirmModal: true,
    }));
  };

  const closeDeletePopup = () => {
    setState((prevState) => ({
      ...prevState,
      deleteConfirmModal: false,
    }));
  };

  const handleUploadSubmit = async (type) => {
    if (type !== 'close') {
      const { imageRes, originalImg } = await childRef.current.showCroppedImg();
      const orignalImageres = await originalImg;

      const dataList = cloneDeep(careGroupDetails);
      dataList.image = imageRes;
      dataList.fullImage = orignalImageres;
      dataList.isRemovedImage = false;
      setState((prevState) => ({
        ...prevState,
        careGroupDetails: dataList,
      }));
    }
    setState((prevState) => ({
      ...prevState,
      uploadImageModal: false,
    }));
  };

  const handleBulkDelete = () => {
    const data = cloneDeep(careGroupDetails);
    const newIdsAfterDelete = data.careLibIds?.filter((eleId) => !checkedIds.includes(eleId));
    data.careLibIds = newIdsAfterDelete;
    setState((prevState) => ({
      ...prevState,
      careGroupDetails: data,
      checkedIds: [],
    }));
  };

  const handleCheckAll = (checkEvent) => {
    const isChecked = checkEvent.target.checked;
    const checkName = checkEvent.target.name;
    const oldCheckedId = checkedIds;
    let newIds = [];

    switch (checkName) {
      case 'check_all_grp_symbol':
        if (isChecked) {
          const newSymbolIds = symbolsId.filter((eleId) => !oldCheckedId.includes(eleId));
          newIds = [...oldCheckedId, ...newSymbolIds];
        } else {
          newIds = oldCheckedId.filter((eleId) => !symbolsId.includes(eleId));
        }
        break;

      case 'check_all_grp_instruction':
        if (isChecked) {
          const newInstIds = instrucionId.filter((eleId) => !oldCheckedId.includes(eleId));
          newIds = [...oldCheckedId, ...newInstIds];
        } else {
          newIds = oldCheckedId.filter((eleId) => !instrucionId.includes(eleId));
        }
        break;

      case 'check_all_grp_disclaimer':
        if (isChecked) {
          const newDIsclaimerIds = disclaimerId.filter((eleId) => !oldCheckedId.includes(eleId));
          newIds = [...oldCheckedId, ...newDIsclaimerIds];
        } else {
          newIds = oldCheckedId.filter((eleId) => !disclaimerId.includes(eleId));
        }
        break;

      default:
        break;
    }

    setState((prevState) => ({
      ...prevState,
      checkedIds: newIds,
    }));
  };

  const handleCheck = (checkEvent, libId) => {
    const existingIds = checkedIds;
    const isLibChecked = checkEvent.target.checked;
    if (isLibChecked && !existingIds.includes(libId)) {
      setState((prevState) => ({
        ...prevState,
        checkedIds: [...existingIds, libId],
      }));
    } else {
      const removedArray = existingIds?.filter((eleId) => eleId !== libId);

      if (checkArray(removedArray)) {
        setState((prevState) => ({
          ...prevState,
          checkedIds: removedArray,
        }));
      } else {
        setState((prevState) => ({
          ...prevState,
          checkedIds: [],
        }));
      }
    }
  };

  const onAutoSuggestion = (suggestion) => {
    if (suggestion?.length > 0) {
      typeaheadRef.current.clear();
      const data = cloneDeep(careGroupDetails);
      if (data?.careLibIds?.includes(suggestion[0]?._id)) {
        toasterMsg(t('CARE_LABELS.GROUP.ADD_EDIT.SELECTED_CARE_LABEL_COMPONENT'), 'danger');
      } else {
        if (!isEmpty(data.careLibIds)) {
          data.careLibIds = [...data.careLibIds, suggestion[0]?._id];
        } else {
          data.careLibIds = [suggestion[0]?._id];
        }
        setState((prevState) => ({
          ...prevState,
          careGroupDetails: data,
        }));
      }
    }
  };

  const isValidData = () => {
    const errorList = [];
    if ('name' in careGroupDetails && careGroupDetails?.name.trim() !== '') {
      /* empty */
    } else {
      errorList.push('name');
    }
    if ('description' in careGroupDetails && careGroupDetails?.description.trim() !== '') {
      /* empty */
    } else {
      errorList.push('description');
    }

    const dataList = cloneDeep(careGroupDetails);
    if (errorList.length === 0) {
      dataList.errorList = [];
      setState((prevState) => ({
        ...prevState,
        careGroupDetails: dataList,
      }));
      return true;
    }
    dataList.errorList = errorList;
    setState((prevState) => ({
      ...prevState,
      careGroupDetails: dataList,
    }));
    return false;
  };

  const saveGroup = async () => {
    const isValid = await isValidData();

    if (isValid) {
      if (isEditMode) {
        const obj = {
          name: careGroupDetails.name.trim(),
          description: careGroupDetails?.description.trim(),
          groupId: careGroupDetails?._id,
          image: careGroupDetails.image,
          fullImage: careGroupDetails.fullImage,
          loggedInUserId: userInfo?.userId,
          careLibIds: JSON.stringify(careGroupDetails?.careLibIds || []),
        };
        if (careGroupDetails?.isRemovedImage) {
          obj.isRemovedImage = careGroupDetails?.isRemovedImage;
        }
        const response = await dispatch(action.updateCareLabelGroup(obj.groupId, obj, t));
        if (response.code === 200) {
          const grpObj = {
            groupId,
            loggedInUserId: userInfo?.userId,
          };
          const getResponse = await dispatch(action.fetchCareLabelGroupByID(groupId, grpObj));
          if (getResponse.code === 200) {
            setState((prevState) => ({
              ...prevState,
              isPageDisable: true,
            }));
          }
        }
      } else {
        const saveObj = {
          name: careGroupDetails?.name.trim(),
          description: careGroupDetails?.description.trim(),
          loggedInUserId: userInfo?.userId,
          image: careGroupDetails?.image,
          fullImage: careGroupDetails?.fullImage,
          careLibIds: JSON.stringify(careGroupDetails?.careLibIds || []),
        };
        const response = await dispatch(action.saveCareLabelGroup(saveObj, t));
        if (response.code === 200) {
          history.push(`/libraries/care-group/edit/${response?.data}`);
        }
      }
    }
  };

  const handleEditPage = () => {
    setState((prevState) => ({
      ...prevState,
      isPageDisable: false,
    }));
  };

  const deleteImageConfirmed = () => {
    const dataList = cloneDeep(careGroupDetails);
    dataList.image = '';
    dataList.fullImage = '';
    dataList.isRemovedImage = true;
    setState((prevState) => ({
      ...prevState,
      careGroupDetails: dataList,
      deleteConfirmModal: false,
    }));
  };

  useEffect(() => {
    const init = async () => {
      await dispatch(
        systemMasterAction.getPCTypesDetails({
          isActive: true,
          tier: 'Tier1',
        }),
      );
    };

    init();
  }, [dispatch]);

  return (
    <>
      <div className="page-title-filter sticky">
        <div className="title-container d-flex align-items-center">
          <h4 className="title">{t('CARE_LABELS.GROUP.ADD_EDIT.CARE_LABEL_GROUP')}</h4>
        </div>
        <div className="search-filter">
          {CareAddEditAccess && isPageDisable ? (
            <button type="button" className="btn btn-white btn-back-lib " onClick={handleEditPage}>
              <i className="icon-edit mr-2" />
              {t('CARE_LABELS.GROUP.ADD_EDIT.EDIT')}
            </button>
          ) : (
            CareAddEditAccess && (
              <button type="button" className="btn btn-white btn-back-lib " onClick={saveGroup}>
                <i className="icon-Save mr-2" />
                {t('CARE_LABELS.GROUP.ADD_EDIT.SAVE')}
              </button>
            )
          )}

          <Link className="btn btn-white btn-back-lib " to="/libraries/care-group">
            {t('CARE_LABELS.GROUP.ADD_EDIT.BACK')}
          </Link>
        </div>
      </div>
      <Container fluid className="pt-4 care-group-add-edit">
        {pageLoader ? (
          <Loader />
        ) : (
          <Row>
            <Col md={12}>
              <Row>
                <Col md={4}>
                  <div className="d-flex align-items-start">
                    <div className="image-container mr-3">
                      <figure>
                        <img src={setImage(careGroupDetails)} className="img-fluid" alt="Broken" />
                      </figure>
                      {!isPageDisable && (
                        <div className="d-flex flex-column">
                          <button className="btn mt-2" type="button" onClick={() => handleImageUpload()}>
                            <span className="icon icon-upload" />
                          </button>
                          {CareDeleteAccess && 'image' in careGroupDetails && careGroupDetails.image !== '' && (
                            <button className="btn mt-4" type="button" onClick={() => handleDeleteImage()}>
                              <span className="icon icon-delete" />
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="flex-fill">
                      <div className="input-group form-floating-label fixed-label">
                        <input
                          type="text"
                          name="name"
                          value={careGroupDetails?.name}
                          onChange={(e) => {
                            handleInput(e);
                          }}
                          className="form-control"
                          disabled={isPageDisable}
                        />
                        <label className="asterisk-req">
                          {t('CARE_LABELS.GROUP.ADD_EDIT.GROUP_NAME')}
                          <span>*</span>
                        </label>
                      </div>
                      {careGroupDetails?.errorList?.includes('name') && <p className="error-message"> {t('CARE_LABELS.GROUP.ADD_EDIT.GROUP_NAME_REQUIRED')}</p>}
                    </div>
                  </div>
                </Col>
                <Col md={4}>
                  <div className="input-group form-floating-label fixed-label">
                    <input
                      type="text"
                      name="description"
                      value={careGroupDetails?.description}
                      onChange={(e) => {
                        handleInput(e);
                      }}
                      maxLength={200}
                      className="form-control"
                      disabled={isPageDisable}
                    />
                    <label className="asterisk-req">
                      {t('CARE_LABELS.GROUP.ADD_EDIT.GROUP_DESCRIPTION')}
                      <span>*</span>
                    </label>
                    <div className="text-count">{careGroupDetails?.description?.length || 0}/200</div>
                  </div>
                  {careGroupDetails?.errorList?.includes('description') && (
                    <p className="error-message">{t('CARE_LABELS.GROUP.ADD_EDIT.GROUP_DESCRIPTION_REQUIRED')}</p>
                  )}
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <div className="group-library-filter mt-4 w-300px">
                    {/* <input type="text" className="form-control text-bordered" placeholder="Search care label library..." /> */}
                    <Typeahead
                      ref={typeaheadRef}
                      id="typeheadWithColours"
                      className="custom-AsyncTypeHead"
                      // isLoading={suggestionLoader}
                      labelKey="code"
                      // labelKey={(option) => `${option?.code}}`}
                      disabled={isPageDisable}
                      minLength={1}
                      options={AllCareLibraryList?.list}
                      promptText={t('CARE_LABELS.GROUP.ADD_EDIT.NO_CARE_LIBRARY_FOUND')}
                      placeholder={t('CARE_LABELS.GROUP.ADD_EDIT.NO_CARE_LIBRARY')}
                      useCache={false}
                      onChange={(selected) => onAutoSuggestion(selected)}
                      renderMenuItemChildren={(option) => (
                        <div className="d-flex" key={option._id}>
                          <div className="list-text">{option?.code ? `${option?.code} - ${option?.description}` : ''}</div>
                        </div>
                      )}
                    />
                    {CareDeleteAccess && (
                      <button type="button" disabled={checkedIds.length === 0} onClick={handleBulkDelete} className="btn btn-outline">
                        <i className="icon-delete" />
                      </button>
                    )}
                  </div>
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <h6 className="mt-4">{t('CARE_LABELS.GROUP.ADD_EDIT.SYMBOL')}</h6>
                  <hr className="my-2 border-dark" />
                  <SymbolTableGroup
                    SymbolData={SymbolData}
                    isAllSymbolChecked={isAllSymbolChecked}
                    handleCheck={handleCheck}
                    handleCheckAll={handleCheckAll}
                    checkedIds={checkedIds}
                    handleDelete={handleDelete}
                    isPageDisable={isPageDisable}
                    CareDeleteAccess={CareDeleteAccess}
                  />
                  <h6 className="mt-4">{t('CARE_LABELS.GROUP.ADD_EDIT.INSTRUCTION')}</h6>
                  <hr className="my-2 border-dark" />
                  <InstructionTableGroup
                    InstructionsData={InstructionsData}
                    isAllInstructionChecked={isAllInstructionChecked}
                    handleCheck={handleCheck}
                    handleCheckAll={handleCheckAll}
                    checkedIds={checkedIds}
                    handleDelete={handleDelete}
                    isPageDisable={isPageDisable}
                    CareDeleteAccess={CareDeleteAccess}
                  />
                  <h6 className="mt-4">{t('CARE_LABELS.GROUP.ADD_EDIT.DISCLAMIER')}</h6>
                  <hr className="my-2 border-dark" />
                  <DisclaimerTableGroup
                    DisclaimerData={DisclaimerData}
                    isAllDisclaimerChecked={isAllDisclaimerChecked}
                    handleCheck={handleCheck}
                    handleCheckAll={handleCheckAll}
                    checkedIds={checkedIds}
                    handleDelete={handleDelete}
                    isPageDisable={isPageDisable}
                    CareDeleteAccess={CareDeleteAccess}
                  />
                </Col>
              </Row>
            </Col>
          </Row>
        )}
      </Container>

      {uploadImageModal && (
        <UploadImageModal
          showUploadModal={uploadImageModal}
          handleUploadSubmit={handleUploadSubmit}
          childRef={childRef}
          image={careGroupDetails?.fullImage || careGroupDetails?.image}
          isEditMode={isEditMode}
        />
      )}

      {deleteConfirmModal && (
        <ConfirmationPopup
          onOpen={deleteConfirmModal}
          onConfirm={deleteImageConfirmed}
          title={t('CARE_LABELS.GROUP.ADD_EDIT.DELETE_GROUP_IMAGE')}
          message={t('CARE_LABELS.GROUP.ADD_EDIT.DELETE_MESSAGE')}
          toggle={closeDeletePopup}
        />
      )}
    </>
  );
}

export default AddEdit;
